/**
 * Who a diarization label belongs to.
 *
 * The label alone does not answer this. When transcript cleanup is rejected by
 * the integrity gate, the pipeline keeps the protected ASR source, whose labels
 * are raw ids (`speaker_0`, `speaker_1`); only when cleanup succeeds are they
 * rewritten into resolved names. The analysis carries the answer either way, in
 * `analysisMetadata.speakerRoles`, resolved by categorize-step against grounded
 * evidence — so that map is the source of truth and this module is the single
 * place that reads it.
 *
 * Matching is done on a normalized key because the API transforms result_json
 * keys to camelCase on the way out: `speaker_0` survives (a digit is not
 * lowercased into the preceding word), but a hypothetical `speaker_a` would
 * arrive as `speakerA` while the diarization still says `speaker_a`.
 */

export type SpeakerRoleValue = 'agent' | 'client' | 'unknown';

export type SpeakerRoleMap = Record<string, SpeakerRoleValue>;

const normalizeLabel = (label: string) => label.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Role for a diarization label, or `undefined` when the map says nothing about
 * it (absent, or explicitly `unknown` — the caller decides what to do with an
 * unresolved speaker; it must not be silently assigned to a side).
 */
export function resolveSpeakerRole(
  speaker: string,
  roles?: SpeakerRoleMap
): Exclude<SpeakerRoleValue, 'unknown'> | undefined {
  if (!roles) return undefined;

  const direct = roles[speaker];
  if (direct) return direct === 'unknown' ? undefined : direct;

  const normalized = normalizeLabel(speaker);
  const found = Object.entries(roles).find(
    ([label]) => normalizeLabel(label) === normalized
  )?.[1];

  return found && found !== 'unknown' ? found : undefined;
}

/**
 * The label→side ladder for analyses stored before `speakerRoles` existed.
 *
 * Two screens read it — the v2 analysis card's talk-ratio bar
 * (`SpeechSection`) and the standalone `SpeakerTalkRatio` — and each carried
 * its own copy. The copies had already diverged on the agent's NAME:
 * SpeechSection folded a label containing it into `agent` (substring), while
 * SpeakerTalkRatio only used the name to pick a COLOUR and matched it exactly,
 * leaving the same person as their own bucket. Two ratio bars on one screen
 * could therefore put one speaker on different sides.
 *
 * Returns `undefined` for a label it cannot place — the caller decides what an
 * unresolved speaker means (unresolved time, or a bucket of its own); it must
 * not be silently assigned to a side.
 */
export function resolveSpeakerSide(
  speaker: string,
  roles?: SpeakerRoleMap,
  agentName?: string
): Exclude<SpeakerRoleValue, 'unknown'> | undefined {
  // The map the pipeline resolved wins over everything else: it was decided
  // against grounded evidence, while the label itself may be a bare
  // `speaker_0` that says nothing about who spoke.
  const mapped = resolveSpeakerRole(speaker, roles);
  if (mapped) return mapped;

  const lower = speaker.toLowerCase();

  if (lower === 'agent' || lower.includes('agent') || lower === 'оператор') {
    return 'agent';
  }
  if (
    lower === 'client' ||
    lower.includes('client') ||
    lower.includes('customer') ||
    lower === 'клієнт' ||
    lower === 'клиент'
  ) {
    return 'client';
  }

  // The record's agent name, which categorize-step writes into the cleaned
  // transcript's labels. Substring, not equality: the label may carry a
  // patronymic, a suffix or surrounding punctuation the record's name field
  // does not.
  const agentNameLower = agentName?.trim().toLowerCase();
  if (agentNameLower && lower.includes(agentNameLower)) return 'agent';

  return undefined;
}
